import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import mensisLogo from '@/assets/mensis-logo.svg';
import { Button } from '@/components/ui/button';
import { BrandFooter } from '@/shared/components/BrandFooter';
import { useAuth } from '@/auth/AuthProvider';
import { useNavigate, Link } from 'react-router-dom';
import { SESSION_FLASH_KEY } from '@/shared/lib/sessionFlash';

const LoginPage: React.FC = () => {
  const auth = useAuth();
  const navigate = useNavigate();

  // Read during render, consume in an effect: StrictMode renders twice in dev and
  // a side effect in the initializer would leave the second render with null.
  const [sessionFlash] = useState<string | null>(() => sessionStorage.getItem(SESSION_FLASH_KEY));

  useEffect(() => {
    sessionStorage.removeItem(SESSION_FLASH_KEY);
  }, []);

  useEffect(() => {
    if (auth.isAuthenticated) {
      navigate('/agreements');
    }
  }, [auth.isAuthenticated, navigate]);

  if (auth.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" aria-hidden="true" />
          <p className="text-sm text-slate-500">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-4">
      <div className="mb-10 flex flex-col items-center text-center">
        <img
          src={mensisLogo}
          alt="Mensis"
          className="mb-4 h-16 w-16"
        />
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">Mensis Trabalhador</h1>
        <p className="mt-2 text-sm text-slate-500">Acesse sua conta para continuar</p>
      </div>

      <div className="w-full max-w-sm space-y-4">
        {sessionFlash && (
          <div role="alert" aria-live="assertive" className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            {sessionFlash}
          </div>
        )}
        {/* The session-expired notice already explains the failed silent renew. */}
        {auth.error && !sessionFlash && (
          <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            Erro de autenticação: {auth.error.message}
          </div>
        )}
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <p className="mb-5 text-sm text-slate-500">
            Faça login com sua conta para acessar o portal de trabalhadores.
          </p>
          <Button className="w-full" size="lg" onClick={() => auth.signinRedirect()}>
            Entrar
          </Button>
        </div>
      </div>

      <p className="mt-6 text-xs text-slate-400">
        Ao continuar você concorda com os{' '}
        <Link to="/terms" className="underline hover:text-slate-600">termos de uso</Link>
        {' '}e a{' '}
        <Link to="/privacy-policy" className="underline hover:text-slate-600">política de privacidade</Link>.
      </p>

      <div className="mt-4">
        <BrandFooter />
      </div>
    </div>
  );
};

export default LoginPage;
